/**
 * WizardContainer
 *
 * Main container for the TurboTax-style guided permit intake wizard.
 * Features a 3-panel layout:
 * - Left: Step navigation sidebar
 * - Center: Main question/form area
 * - Right: Context & help panel
 */

import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useWizardState } from '@/hooks/useWizardState';
import { useWizardData } from '@/hooks/useWizardData';
import { useProjects } from '@/hooks/useProject';
import { type WizardStep } from '@/types/wizard';
import { WizardSidebar } from './WizardSidebar';
import { WizardHelp } from './WizardHelp';
import { WizardStepEntry } from './steps/WizardStepEntry';
import { WizardStepProjectType } from './steps/WizardStepProjectType';
import { WizardStepProjectBasics } from './steps/WizardStepProjectBasics';
import { WizardStepUnits } from './steps/WizardStepUnits';
import { WizardStepBasics } from './steps/WizardStepBasics';
import { WizardStepTrades } from './steps/WizardStepTrades';
import { WizardStepScopes } from './steps/WizardStepScopes';
import { WizardStepPermits } from './steps/WizardStepPermits';
import { WizardStepDetails } from './steps/WizardStepDetails';
import { WizardStepReview } from './steps/WizardStepReview';
import { Save, X, ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface WizardContainerProps {
  onComplete?: (projectId: string, jobIds: string[]) => void;
  onCancel?: () => void;
}

export function WizardContainer({ onComplete, onCancel }: WizardContainerProps) {
  const navigate = useNavigate();
  const [showExitDialog, setShowExitDialog] = useState(false);

  // Wizard state management
  const wizard = useWizardState({ autoLoad: true });

  // Wizard data (trade types, job scopes, permit types)
  const { tradeTypes, isLoading: dataLoading } = useWizardData({
    selectedTradeIds: wizard.state.trades.selectedTradeIds,
  });

  // Actually perform the exit
  const performExit = useCallback(() => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/permits');
    }
  }, [navigate, onCancel]);

  // Handle exit/cancel - show confirmation if there are unsaved changes
  const handleExit = useCallback(() => {
    if (wizard.hasUnsavedChanges) {
      setShowExitDialog(true);
    } else {
      performExit();
    }
  }, [wizard.hasUnsavedChanges, performExit]);

  // Save and exit
  const handleSaveAndExit = useCallback(async () => {
    await wizard.saveDraft();
    setShowExitDialog(false);
    performExit();
  }, [wizard, performExit]);

  // Exit without saving
  const handleExitWithoutSaving = useCallback(() => {
    wizard.discardChanges();
    setShowExitDialog(false);
    performExit();
  }, [wizard, performExit]);

  // Handle wizard completion
  const handleComplete = useCallback((projectId: string, jobIds: string[]) => {
    // Clear the draft
    wizard.clearDraft();

    if (onComplete) {
      onComplete(projectId, jobIds);
    } else {
      // Navigate to the first created job
      if (jobIds.length > 0) {
        navigate(`/jobs/${jobIds[0]}`);
      } else {
        navigate('/permits');
      }
    }
  }, [wizard, navigate, onComplete]);

  // Handle mode selection from entry step
  const handleModeSelect = useCallback((mode: 'single_permit' | 'development_project') => {
    wizard.setMode(mode);
    wizard.goNext();
  }, [wizard]);

  // Render current step content
  const renderStepContent = () => {
    switch (wizard.currentStep) {
      // Entry step (both modes)
      case 'entry':
        return (
          <WizardStepEntry
            selectedMode={wizard.mode}
            onSelectMode={handleModeSelect}
          />
        );

      // Development project specific steps
      case 'project_type':
        return (
          <WizardStepProjectType
            data={wizard.state.project}
            onUpdate={wizard.updateProject}
          />
        );
      case 'project_basics':
        return (
          <WizardStepProjectBasics
            projectData={wizard.state.project}
            basicsData={wizard.state.basics}
            onUpdateProject={wizard.updateProject}
            onUpdateBasics={wizard.updateBasics}
          />
        );
      case 'units':
        return (
          <WizardStepUnits
            data={wizard.state.project}
            onUpdate={wizard.updateProject}
          />
        );

      // Shared steps (both modes)
      case 'basics':
        return (
          <WizardStepBasics
            data={wizard.state.basics}
            onUpdate={wizard.updateBasics}
          />
        );
      case 'trades':
        return (
          <WizardStepTrades
            data={wizard.state.trades}
            onUpdate={wizard.updateTrades}
            tradeTypes={tradeTypes}
            contractorRole={wizard.state.basics.contractorRole}
          />
        );
      case 'scopes':
        return (
          <WizardStepScopes
            data={wizard.state.scopes}
            onUpdate={wizard.updateScopes}
            tradesData={wizard.state.trades}
          />
        );
      case 'permits':
        return (
          <WizardStepPermits
            data={wizard.state.permits}
            onUpdate={wizard.updatePermits}
            scopesData={wizard.state.scopes}
          />
        );
      case 'details':
        return (
          <WizardStepDetails
            data={wizard.state.details}
            onUpdate={wizard.updateDetails}
            basicsData={wizard.state.basics}
            permitsData={wizard.state.permits}
          />
        );
      case 'review':
        return (
          <WizardStepReview
            state={wizard.state}
            onComplete={handleComplete}
          />
        );
      default:
        return null;
    }
  };

  // Get current step config
  const currentStepConfig = wizard.steps.find((s) => s.id === wizard.currentStep);

  // Loading state
  if (wizard.isLoading || dataLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">Loading wizard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-background flex flex-col overflow-hidden">
      {/* Header */}
      <header className="border-b bg-card px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <h1 className="text-lg font-semibold">
            {wizard.mode === 'development_project' ? 'Development Project Wizard' : 'Guided Permit Intake'}
          </h1>
          <div className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground">
            <span>Step {wizard.stepNumber} of {wizard.totalSteps}</span>
            <Progress
              value={wizard.getCompletionPercentage()}
              className="w-24 h-2"
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={wizard.saveDraft}
            disabled={wizard.isSaving}
          >
            {wizard.isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span className="ml-2 hidden sm:inline">Save Draft</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={handleExit}>
            <X className="h-4 w-4" />
            <span className="ml-2 hidden sm:inline">Exit</span>
          </Button>
        </div>
      </header>

      {/* Main Content - 3 Panel Layout */}
      <div className="flex-1 flex min-h-0">
        {/* Left Sidebar - Step Navigation */}
        <WizardSidebar
          steps={wizard.steps}
          currentStep={wizard.currentStep}
          completedSteps={wizard.state.completedSteps}
          visitedSteps={wizard.state.visitedSteps}
          onStepClick={wizard.goToStep}
          mode={wizard.mode}
        />

        {/* Center - Main Content */}
        <main className="flex-1 overflow-y-auto p-6 min-h-0 isolate">
          <div className="max-w-2xl mx-auto">
            {/* Step Title - hidden for entry step which has its own title */}
            {currentStepConfig && wizard.currentStep !== 'entry' && (
              <div className="mb-6">
                <h2 className="text-2xl font-bold">{currentStepConfig.title}</h2>
                <p className="text-muted-foreground mt-1">
                  {currentStepConfig.description}
                </p>
              </div>
            )}

            {/* Step Content */}
            <div className="space-y-6">
              {renderStepContent()}
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t">
              <Button
                variant="outline"
                onClick={wizard.goPrev}
                disabled={!wizard.canGoPrev}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>

              {wizard.currentStep !== 'review' ? (
                <Button onClick={wizard.goNext} disabled={!wizard.canGoNext}>
                  Continue
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              ) : null}
            </div>
          </div>
        </main>

        {/* Right Panel - Context & Help */}
        <WizardHelp
          currentStep={wizard.currentStep}
          basicsData={wizard.state.basics}
        />
      </div>

      {/* Exit Confirmation Dialog */}
      <AlertDialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Exit without saving?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Would you like to save your progress before leaving?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel onClick={() => setShowExitDialog(false)}>
              Cancel
            </AlertDialogCancel>
            <Button
              variant="outline"
              onClick={handleExitWithoutSaving}
            >
              Exit Without Saving
            </Button>
            <AlertDialogAction onClick={handleSaveAndExit}>
              <Save className="h-4 w-4 mr-2" />
              Save Draft & Exit
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
